import PersonAddressComponents from "./PersonAddressComponents";
import { Add as AddIcon } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Stack,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import * as logger from "common/logger";
import * as objects from "common/objects";
import Component from "components/Component";
import Button from "components/common/Button";
import TextField from "components/common/TextField";
import Visible from "components/common/Visible";
import * as config from "config";
import { composeAddress, hasAddressComponents } from "domain/address";
import { BROKER_ADDRESS_COMPONENTS } from "flags";
import * as _ from "lodash";
import { useState } from "react";
import { useSelector } from "react-redux";
import * as userSelectors from "store/selectors/user/userSelectors";

const SelectFromChoices = ({
  label,
  value,
  onChange,
  choices,
  onAddChoice,
}) => {
  return (
    <Autocomplete
      options={choices}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          type={"text"}
          variant={"filled"}
          InputLabelProps={{ shrink: true }}
          label={label}
          fullWidth
        />
      )}
      filterOptions={(options, state) => {
        const inputValue = (state?.inputValue ?? "").trim();
        const filteredOptions = objects
          .search(
            options.map((x) => ({ id: x })),
            {
              search: inputValue.split(/\s+/),
              keys: ["id"],
            }
          )
          .map((x) => x.id);
        return onAddChoice && inputValue.length
          ? [...filteredOptions, { addNew: true, value: inputValue }]
          : filteredOptions;
      }}
      getOptionLabel={(x) => (x?.addNew ? `Add New: ${x.value}` : x)}
      renderOption={(props, option, state) => {
        if (option.addNew) {
          return (
            <Box {...props} sx={{ margin: 1 }}>
              <Stack direction={"row"} alignItems={"center"} gap={1}>
                <AddIcon />
                <Typography>
                  {"Add New: "}
                  {option.value}
                </Typography>
              </Stack>
            </Box>
          );
        } else {
          return (
            <Box {...props} sx={{ margin: 1 }}>
              <Typography>{option}</Typography>
            </Box>
          );
        }
      }}
      value={value}
      onChange={(_, value, reason) => {
        if (reason === "selectOption" && value) {
          if (value.addNew && value.value) {
            onAddChoice && onAddChoice(value.value);
          } else {
            onChange(value);
          }
        } else if (reason === "clear") {
          onChange(null);
        }
      }}
    />
  );
};

const BrokerCreationTemplate = ({
  brokers,
  createBroker,
  defaultVisibilityOverrideById,
  fieldLabels,
  visibilityBaseTag,
  formId,
  title,
  entityName = "broker",
  onCreate,
  onClose,
}) => {
  const [brokerInputs, setBrokerInputs] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  // `BROKER_ADDRESS_COMPONENTS` is a component-visibility tag registered in `flags.js`,
  // so it must be read from `componentVisibility` (the same store `Visible` reads),
  // not from the per-company `feature_flags` config. Defaults to off when untagged.
  const isVisible = useSelector(userSelectors.selectIsVisible);
  const useAddressComponents = isVisible(BROKER_ADDRESS_COMPONENTS) ?? false;

  const [companies, setCompanies] = useState(() =>
    _.chain(brokers ?? [])
      .map((x) => x.company)
      .filter((x) => x != null)
      .uniq()
      .sort()
      .value()
  );

  const BROKER_FIELDS = [
    {
      id: "name",
      label: "Contact Name",
      defaultVisibility: true,
    },
    {
      id: "email",
      label: "Email",
      defaultVisibility: true,
    },
    {
      id: "workNumber",
      label: "Work Number",
      defaultVisibility: true,
    },
    {
      id: "mobileNumber",
      label: "Mobile Number",
      defaultVisibility: true,
    },
    {
      id: "company",
      label: "Company",
      choices: companies,
      defaultVisibility: true,
      onAddChoice: (choice) =>
        setCompanies(
          _.chain([...companies, choice])
            .uniq()
            .sort()
            .value()
        ),
    },
    {
      id: "address",
      label: "Address",
      rows: 3,
      defaultVisibility: false,
    },
    {
      id: "licenseNumber",
      label: "License Number",
      defaultVisibility: false,
    },
    {
      id: "npn",
      label: "NPN",
      defaultVisibility: false,
    },
    {
      id: "location",
      label: "Location",
      defaultVisibility: true,
    },
  ].map((field) => ({
    ...field,
    label: fieldLabels?.[field.id] ?? field.label,
  }));

  const saveBroker = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const { data: broker, error } = await createBroker(brokerInputs);
      if (error) {
        logger.exception(error);
        setError(`Failed to create a new ${entityName}`);
      } else {
        onCreate(broker);
      }
    } catch (error) {
      logger.exception(error);
      setError(`Failed to create a new ${entityName}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Component title={title}>
      <form id={formId}>
        <Stack direction={"column"} spacing={config.GRID_SPACING}>
          {BROKER_FIELDS.map((field) => {
            const value = brokerInputs[field.id];
            const updateValue = (newValue) =>
              setBrokerInputs({
                ...brokerInputs,
                [field.id]: newValue || undefined,
              });

            // When the structured-address tag is on, the component fields replace the
            // single Address field (keeping a composed single-line address in sync).
            // This sits inside the field's own `Visible` so hiding the address field
            // hides the structured form of it too — several callers default it off.
            const replaceWithAddressComponents =
              field.id === "address" && useAddressComponents;

            return (
              <Visible
                key={field.id}
                byTag={`${visibilityBaseTag ?? "<BrokerCreation>"}/${field.id}`}
                defaultVisible={
                  defaultVisibilityOverrideById?.[field.id] ??
                  field.defaultVisibility
                }
              >
                {replaceWithAddressComponents ? (
                  <PersonAddressComponents
                    components={brokerInputs.addressComponents}
                    onChange={(components) =>
                      setBrokerInputs({
                        ...brokerInputs,
                        // Don't persist a components object of nothing but "".
                        addressComponents: hasAddressComponents(components)
                          ? components
                          : undefined,
                        address: composeAddress(components) || undefined,
                      })
                    }
                  />
                ) : field.choices != null ? (
                  <SelectFromChoices
                    label={field.label}
                    value={value ?? null}
                    onChange={updateValue}
                    choices={field.choices}
                    onAddChoice={(choice) => {
                      field.onAddChoice(choice);
                      updateValue(choice);
                    }}
                  />
                ) : (
                  <TextField
                    label={field.label}
                    value={value ?? ""}
                    onChange={(x) => updateValue(x.target.value)}
                    multiline={!!field.rows}
                    rows={field.rows}
                  />
                )}
              </Visible>
            );
          })}
          <Stack direction={"row"} spacing={1} justifyContent={"flex-end"}>
            <Button
              color={"secondary"}
              onClick={() => onClose()}
              isDisabled={isSaving}
            >
              {"Cancel"}
            </Button>
            <Button onClick={saveBroker} isLoading={isSaving}>
              {"Save"}
            </Button>
          </Stack>
          {error && <Alert severity={"error"}>{error}</Alert>}
        </Stack>
      </form>
    </Component>
  );
};

export default BrokerCreationTemplate;
